import {
  AuthenticateOptions,
  AuthenticationEncoded,
  RegisterOptions,
  RegistrationEncoded,
} from "@passwordless-id/webauthn/dist/esm/types";
// import { client } from "@passwordless-id/webauthn";
// import { v4 as uuidv4 } from "uuid";

import { request } from "@gm-network/request";
import {
  decryptAesCbc,
  decryptRasOaep,
  exportPublicKey,
  generateKey,
  importKey,
  getClientValue,
} from "../utils";

import { Log } from "./log";
import { GMAccountAbstraction } from "./smart-account";
import {
  GMCore,
  ChainInfo,
  Config,
  ConnectConfig,
  CreateConfig,
  Options,
  defaultSupportedChains,
  AAProvider,
  AccountAbstraction,
  WalletEventName,
  walletEvents,
  events,
  Network,
  Chains,
} from "../types";

interface Passkey {
  isAvailable(): boolean;
  isLocalAuthenticator(): Promise<boolean>;
  register(
    username: string,
    challenge: string,
    options?: RegisterOptions
  ): Promise<RegistrationEncoded>;
  authenticate(
    credentialIds: string[],
    challenge: string,
    options?: AuthenticateOptions
  ): Promise<AuthenticationEncoded>;
}
interface Emitter {
  emit(event: string, v: any): void;
  on(
    eventName: WalletEventName | string,
    listener: (...args: any[]) => void
  ): void;
  removeListener(
    event: WalletEventName | string,
    listener: (...args: any[]) => void
  ): void;
}

const Ethereum: ChainInfo = {
  id: 1,
  name: "Ethereum",
  chainType: "evm",
  icon: "https://static.particle.network/token-list/ethereum/native.png",
  nativeIcon: "",
  fullname: "Ethereum Mainnet",
  network: "Mainnet",
  website: "https://ethereum.org",
  nativeCurrency: {
    name: "Ether",
    symbol: "ETH",
    decimals: 18,
  },
  rpcUrl: "https://ethereum.publicnode.com",
  blockExplorerUrl: "https://etherscan.io",
  features: [{ name: "EIP1559" }],
};

export class InnerCore extends GMCore {
  public readonly isGMNetwork: boolean = true;
  private declare readonly _baseUrl: string;
  private declare readonly _isDev: boolean;
  private declare readonly _config: Config;
  private declare readonly _defaultChainId: number;
  private declare readonly _supportedChainIds: number[];
  private declare _emitter: Emitter;
  private declare _network: Network; // particle_auth.ParticleNetwork;
  private declare _passkey: Passkey;
  private declare _chains: Chains; // particle_chains.chains
  private declare _smartAccount: GMAccountAbstraction | null | undefined;

  constructor(config: Config, options?: Options) {
    super();

    const isDev = options?.debug || false;
    Log._isDev = isDev;
    if (!config.projectId || !config.clientKey || !config.appId) {
      Log.e(
        "GMCore constructor error",
        "Missing required parameters for initialization"
      );
      return;
    }

    this._isDev = isDev;
    this._baseUrl = isDev
      ? "https://test-api-id.gmnetwork.ai"
      : "https://api-id.gmnetwork.ai";
    this._config = config;
    this._defaultChainId = options?.defaultChainId || 1;
    this._supportedChainIds = !!options?.supportedChainIds?.length
      ? options?.supportedChainIds
      : Object.values(defaultSupportedChains);

    this._initial().catch((e) => Log.e("initial error", e));
  }

  /**
   * *** private functions ***
   */
  private async _initial(): Promise<void> {
    const EventEmitter = (await import("eventemitter3")).default;
    this._emitter = new EventEmitter();

    const webauthn = await import("@passwordless-id/webauthn");
    this._passkey = webauthn.client;
    const particle_chains = await import("@particle-network/chains");
    const { chains } = particle_chains;
    this._chains = chains;

    const defaultChain =
      this._chains?.getEVMChainInfoById?.(this._defaultChainId) || Ethereum;

    Log.d("initial chain", {
      // methods: this._chains,
      chainId: this._defaultChainId,
      info: defaultChain,
    });

    const particle_auth = await import("@particle-network/auth");
    this._network = new particle_auth.ParticleNetwork({
      projectId: this._config.projectId!,
      clientKey: this._config.clientKey!,
      appId: this._config.appId!,
      chainName: defaultChain.name,
      chainId: defaultChain.id,
      wallet: {
        preload: false,
        displayWalletEntry: false,
        defaultWalletEntryPosition: particle_auth.WalletEntryPosition.BR,
        uiMode: "light",
        customStyle: {
          fiatCoin: "USD",
        },
      },
      preload: true,
      securityAccount: {
        promptSettingWhenSign: particle_auth.SettingOption.Once,
        promptMasterPasswordSettingWhenLogin: particle_auth.SettingOption.Once,
      },
    });

    // window.particle?.auth.off("chainChanged", () => {});
    // window.particle?.auth.off("disconnect", () => {});
    // window.particle?.walletEntryDestroy();
    this._network.auth.setChainInfo(defaultChain);

    if (this._isAuthConnected) {
      await this._createSmartAccount();
    }

    this._network.auth.on(events.connect, () => {
      Log.d("connect auth");
    });
    this._network.auth.on(events.disconnect, (v: any) => {
      Log.d("disconnect auth");
      this._smartAccount = null;
      this._emitter.emit(events.disconnect, v);
    });
  }

  private async _login(callOptions?: ConnectConfig) {
    if (!(await this.isAvailable())) {
      throw new Error(
        "The @gm-network/gmid failed to verify the passkey. Please ensure that the device supports passkey."
      );
    }

    const options = await request({
      method: "POST",
      apiPath: "/webauthn/authentication_options/",
      domain: this._baseUrl,
    });
    if (!!options.error || !options.data?.success) {
      throw new Error(options.data?.error_message || options.error?.message);
    }

    const challenge: string = options.data?.result?.challenge;
    const authenticationEncoded = await this._passkey!.authenticate(
      [],
      challenge,
      {
        authenticatorType: "auto",
        userVerification: "required",
        timeout: 60000,
        debug: this._isDev,
      }
    );

    const randomKey = await generateKey();
    const publicKey = await exportPublicKey(randomKey!.publicKey);
    const data = {
      credential: JSON.stringify({
        id: authenticationEncoded.credentialId,
        rawId: authenticationEncoded.credentialId,
        type: "public-key",
        response: {
          clientDataJSON: authenticationEncoded.clientData,
          authenticatorData: authenticationEncoded.authenticatorData,
          signature: authenticationEncoded.signature,
          userHandle: authenticationEncoded.userHandle,
        },
      }),
      challenge,
      rp_id:
        callOptions?.rp_id ||
        getClientValue("location")?.hostname ||
        "gmnetwork.ai",
      origin:
        callOptions?.origin ||
        getClientValue("location")?.origin ||
        "https://gmnetwork.ai",
      key: publicKey,
    };

    const verified = await request({
      method: "POST",
      apiPath: "/webauthn/authentication_verification/",
      domain: this._baseUrl,
      options: { params: data },
    });
    if (
      !!verified.error ||
      !verified.data?.success ||
      !verified.data?.result?.jwt?.length
    ) {
      throw new Error(
        verified.data?.error_message ||
          verified.error?.message ||
          "Passkey validation failed"
      );
    }

    const verifiedResult = verified.data?.result;
    const rasExtra = await decryptRasOaep(
      randomKey!.privateKey,
      verifiedResult.key
    );
    const jwt = await decryptAesCbc(
      await importKey(rasExtra),
      verifiedResult.jwt
    );
    if (!jwt?.length) {
      throw new Error("Passkey validation failed");
    }

    return await this._authLogin(jwt);
  }

  private async _register(callOptions?: CreateConfig) {
    if (!(await this.isAvailable())) {
      throw new Error(
        "The @gm-network/gmid failed to verify the passkey. Please ensure that the device supports passkey."
      );
    }

    const options = await request({
      method: "POST",
      apiPath: "/webauthn/registration_options/",
      domain: this._baseUrl,
    });
    if (!!options.error || !options.data?.success) {
      throw new Error(options.data?.error_message || options.error?.message);
    }

    const challenge: string = options.data?.result?.challenge;
    let userHandle = callOptions?.userHandle;
    if (!userHandle) {
      userHandle = (await import("uuid")).v4();
    }
    const registrationEncoded = await this._passkey!.register(
      callOptions?.username || "GMID",
      challenge,
      {
        authenticatorType: "auto",
        userVerification: "required",
        timeout: 60000,
        attestation: true,
        userHandle: userHandle,
        debug: this._isDev,
      }
    );

    const randomKey = await generateKey();
    const publicKey = await exportPublicKey(randomKey!.publicKey);
    const data = {
      credential: JSON.stringify({
        id: registrationEncoded.credential.id,
        rawId: registrationEncoded.credential.id,
        response: {
          attestationObject: registrationEncoded.attestationData,
          clientDataJSON: registrationEncoded.clientData,
          transports: ["hybrid", "internal"],
          publicKeyAlgorithm: -7,
          publicKey: registrationEncoded.credential.publicKey,
          authenticatorData: registrationEncoded.authenticatorData,
        },
        type: "public-key",
        clientExtensionResults: { credProps: { rk: "true" } },
        authenticatorAttachment: "platform",
      }),
      challenge,
      rp_id: callOptions?.rp_id || getClientValue("location")?.hostname,
      origin: callOptions?.origin || getClientValue("location")?.origin,
      key: publicKey,
    };

    const verified = await request({
      method: "POST",
      apiPath: "/webauthn/registration_verification/",
      domain: this._baseUrl,
      options: { params: data },
    });
    if (
      !!verified.error ||
      !verified.data?.success ||
      !verified.data?.result?.verified ||
      !verified.data?.result?.jwt?.length
    ) {
      throw new Error(
        verified.data?.error_message ||
          verified.error?.message ||
          "Passkey validation failed"
      );
    }

    const verifiedResult = verified.data?.result;
    const rasExtra = await decryptRasOaep(
      randomKey!.privateKey,
      verifiedResult.key
    );
    const jwt = await decryptAesCbc(
      await importKey(rasExtra),
      verifiedResult.jwt
    );
    if (!jwt?.length) {
      throw new Error("Passkey validation failed");
    }

    return await this._authLogin(jwt);
  }

  private async _authLogin(jwt: string) {
    await this._network.auth.login({
      preferredAuthType: "jwt",
      account: jwt,
      hideLoading: true,
    });
    return await this._createSmartAccount();
  }

  private async _createSmartAccount() {
    const aa = new GMAccountAbstraction(this._network.auth);
    const userinfo = await aa
      .connect({
        projectId: this._config.projectId!,
        clientKey: this._config.clientKey!,
        appId: this._config.appId!,
        supportedChainIds: this.supportedChainIds,
      })
      .catch((error) => {
        Log.e("wallet initial error", error);
        return null;
      });
    if (userinfo != null) {
      this._smartAccount = aa;
      this._emitter.emit(events.connect, userinfo);
    } else {
      this._smartAccount = null;
      // this._eventEmitter.emit(events.disconnect, userinfo);
    }
    return userinfo;
  }

  private get _isAuthConnected() {
    if (!this._network) {
      return false;
    }
    return this._network.auth.isLogin();
  }

  private _throwInitialError(message: string) {
    if (!this._network) {
      Log.e(
        message,
        "@gm-network/gmid initialization parameters error, please ensure that the config is correctly set."
      );
      throw new Error(
        "@gm-network/gmid initialization parameters error, please ensure that the config is correctly set."
      );
    }
  }

  /**
   * *** base config methods ***
   */
  public get wallet(): AccountAbstraction | null | undefined {
    return this._smartAccount;
  }

  public get provider(): AAProvider | null | undefined {
    return this._smartAccount?.provider;
  }

  public get connected(): boolean {
    return this._isAuthConnected && this._smartAccount != null;
  }

  public async isAvailable(): Promise<boolean> {
    if (this._passkey == null) return false;
    if (!this._passkey.isAvailable()) return false;
    return await this._passkey.isLocalAuthenticator();
  }

  public async create(callOptions?: CreateConfig): Promise<boolean> {
    this._throwInitialError("create error");

    try {
      if (this._isAuthConnected) {
        await this.disconnect();
      }
      const user = await this._register(callOptions);

      return user != null;
    } catch (e) {
      Log.e("create error", e);
      throw e;
    }
  }

  public async connect(callOptions?: ConnectConfig): Promise<boolean> {
    this._throwInitialError("connect error");

    try {
      let user: any;
      if (!this._isAuthConnected) {
        user = await this._login(callOptions);
      } else if (this._smartAccount == null) {
        user = await this._createSmartAccount();
      }

      return user != null;
    } catch (e) {
      Log.e("connect error", e);
      throw e;
    }
  }

  public async disconnect(): Promise<boolean> {
    this._throwInitialError("disconnect error");

    try {
      if (this._isAuthConnected) {
        await this._network.auth.logout(true);
      }
      this._smartAccount = null;
      return true;
    } catch (e) {
      Log.e("disconnect error", e);
      throw e;
    }
  }

  public get chainId(): number {
    this._throwInitialError("chainId error");

    return this._network.auth.getChainId();
  }
  public get chainInfo(): ChainInfo {
    this._throwInitialError("setChainId error");

    return this._network.auth.getChain();
  }

  public setChainId(chainId: number): boolean {
    this._throwInitialError("setChainId error");

    const info = this._chains.getEVMChainInfoById(chainId);

    if (info == null) {
      Log.e("setChainId error", "Unsupported chain");
      throw new Error("Unsupported chain");
    }

    this._network.auth.setChainInfo(info);
    return true;
  }

  public getChainInfo(chainId: number): ChainInfo {
    const info = this._chains.getEVMChainInfoById(chainId);

    if (info == null) {
      Log.e("getChainInfo error", "Unsupported chain");
      throw new Error("Unsupported chain");
    }

    return info;
  }

  public get supportedChainIds(): number[] {
    return this._supportedChainIds;
  }

  public get supportedChains(): ChainInfo[] {
    return this._chains
      .getAllChainInfos()
      .filter(
        (v: ChainInfo) =>
          v.chainType === "evm" && this._supportedChainIds.includes(v.id)
      );
  }

  /**
   * *** common event methods ***
   */
  public on(
    eventName: WalletEventName | string,
    listener: (...args: any[]) => void
  ) {
    if (!walletEvents.includes(eventName)) {
      this._network.auth.on(eventName, listener);
      return;
    }

    this._emitter.on(eventName, listener);
  }

  public removeListener(
    eventName: WalletEventName | string,
    listener: (...args: any[]) => void
  ) {
    if (!walletEvents.includes(eventName)) {
      this._network.auth.removeListener(eventName, listener);
      return;
    }

    this._emitter.removeListener(eventName, listener);
  }

  public get key() {
    return this._config.projectId + this._config.clientKey + this._config.appId;
  }
}
export default InnerCore;
